// Shared fetch core for the standalone app bundles (apps/library,
// apps/admin, apps/store). Each app previously hand-rolled its own
// request() + query-string builder + error handling with subtly
// diverging semantics; this module consolidates the common core while
// leaving per-app divergence (base path, array-query support, error-body
// parsing strategy, endpoints that bypass request() entirely) in each
// app's own src/api.js wrapper. See that file's JSDoc for what's still
// app-specific and why.
//
// Browser-safe (no DOM access at module top level) so it can be imported
// by client bundles; kept free of Node APIs so a server import stays safe
// too, per repo convention for src/shared/.

/**
 * Thrown by request() on any non-2xx response. `status` is the HTTP
 * status code; `body` is the parsed error payload (shape depends on
 * `opts.errorBodyAs` — see request()). Matches the ApiError class
 * independently defined (byte-for-byte, pre-refactor) in
 * apps/library/src/api.js and apps/store/src/api.js: both apps'
 * exported `ApiError` now IS this class, so `instanceof` checks at
 * their call sites keep working unchanged.
 */
export class ApiError extends Error {
  constructor(status, body, message) {
    super(message || (body?.error || `HTTP ${status}`));
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

/**
 * Build a URL query string from a params object, matching the
 * strictest of the three apps' pre-refactor query builders (library's):
 *   - `undefined`, `null`, and `''` values are skipped entirely (no
 *     `key=` with an empty value ever appears).
 *   - Array values are appended once per item (`params.append`), so
 *     `{ tag: ['a', 'b'] }` produces `tag=a&tag=b`, not a single
 *     comma-joined value.
 *   - Everything else is coerced with `String(v)` and set once.
 * Returns the query string with NO leading '?' (empty string if no
 * params survive), so callers control how it's joined onto a path.
 */
export function buildQuery(params = {}) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach(item => usp.append(k, item));
    else usp.set(k, String(v));
  }
  return usp.toString();
}

/**
 * Core fetch wrapper shared by the three apps' api.js modules.
 *
 * opts:
 *   - method       default 'GET'.
 *   - body         JSON-stringified and sent with a
 *                   'Content-Type: application/json' header whenever it
 *                   is not `undefined` (any method, including DELETE —
 *                   apps/admin's role-revocation calls rely on this).
 *   - query        object passed through buildQuery(); appended to the
 *                   path as `?...` only if the built string is non-empty.
 *   - signal       passed straight through to fetch() for AbortController
 *                   cancellation.
 *   - headers      extra headers merged in (Content-Type from `body`
 *                   still wins/applies independently).
 *   - basePath     prepended to `path` verbatim (e.g. '/api/library',
 *                   '/api/store'). Defaults to '' — admin's routes are
 *                   already-complete paths with no shared prefix, and its
 *                   wrapper omits this option entirely.
 *   - errorBodyAs  'json' (default) or 'text'. Controls how the body of
 *                   a non-2xx response is read before being attached to
 *                   the thrown ApiError:
 *                     'json' — try res.json(), fall back to `null` if the
 *                       response isn't valid JSON. This is library's and
 *                       store's original behavior.
 *                     'text' — always res.text() (falling back to `''`
 *                       if even that throws), never attempts res.json().
 *                       This is admin's original behavior, where
 *                       `err.body` was always a string.
 *
 * On success: a 204 response returns `null`; otherwise the response is
 * parsed as JSON if its content-type includes 'application/json', else
 * returned as text. On failure: throws ApiError(status, body) per the
 * errorBodyAs strategy above.
 */
export async function request(path, opts = {}) {
  const {
    method = 'GET',
    body,
    query,
    signal,
    headers,
    basePath = '',
    errorBodyAs = 'json',
  } = opts;

  let url = `${basePath}${path}`;
  if (query) {
    const qs = buildQuery(query);
    if (qs) url += `?${qs}`;
  }

  const init = {
    method,
    credentials: 'include',
    headers: { ...headers },
    signal,
  };
  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url, init);
  if (!res.ok) {
    let parsedBody;
    if (errorBodyAs === 'text') {
      parsedBody = await res.text().catch(() => '');
    } else {
      parsedBody = null;
      try { parsedBody = await res.json(); } catch { /* not JSON */ }
    }
    throw new ApiError(res.status, parsedBody);
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res.text();
}
